type ElectronDisplay = {
  id?: number;
  scaleFactor?: number;
  bounds?: { width?: number; height?: number };
  workArea?: { width?: number; height?: number };
  size?: { width?: number; height?: number };
  workAreaSize?: { width?: number; height?: number };
};

type ElectronDisplayInfo = {
  /**
   * Display that currently contains the app window. In mixed-DPI dual-monitor
   * setups this must be preferred over the primary display, otherwise a 4K
   * primary monitor keeps the renderer in 4K density even after the window is
   * moved to a 100% FHD monitor.
   */
  current?: ElectronDisplay;
  primary?: ElectronDisplay;
  displays?: ElectronDisplay[];
};

type DisplayRuntimeProfile = {
  initializedAt: number;
  devicePixelRatio: number;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  electronScaleFactor: number;
  electronDisplayWidth: number;
  electronDisplayHeight: number;
  physicalScreenWidth: number;
  physicalScreenHeight: number;
  uiScale: number;
  typographyScale: number;
  displayDensity: 'compact' | 'normal' | 'large' | '4k';
  ffrChartScale: number;
};

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const roundHalf = (v: number) => Math.round(v * 2) / 2;

const BASE_TEXT_SIZES: Record<string, number> = {
  '--text-8': 8,
  '--text-9': 9,
  '--text-10': 10,
  '--text-11': 11,
  '--text-12': 12,
  '--text-13': 13,
  '--text-14': 14,
  '--text-15': 15,
  '--text-16': 16,
  '--text-18': 18,
  '--text-20': 20,
  '--text-22': 22,
  '--text-24': 24,
  '--text-26': 26,
  '--text-28': 28,
  '--text-30': 30,
  '--text-32': 32,
};

const COMPACT_TEXT_SIZES: Record<string, number> = {
  '--text-8': 8,
  '--text-9': 9,
  '--text-10': 10,
  '--text-11': 10.5,
  '--text-12': 11,
  '--text-13': 11.5,
  '--text-14': 12,
  '--text-15': 13,
  '--text-16': 14,
  '--text-18': 16,
  '--text-20': 18,
  '--text-22': 20,
  '--text-24': 22,
  '--text-26': 24,
  '--text-28': 26,
  '--text-30': 28,
  '--text-32': 30,
};

const LARGE_TEXT_SIZES: Record<string, number> = {
  '--text-8': 9.5,
  '--text-9': 10.5,
  '--text-10': 11.5,
  '--text-11': 12.5,
  '--text-12': 13.5,
  '--text-13': 14.5,
  '--text-14': 16,
  '--text-15': 17,
  '--text-16': 18,
  '--text-18': 20,
  '--text-20': 23,
  '--text-22': 25,
  '--text-24': 27,
  '--text-26': 29,
  '--text-28': 31,
  '--text-30': 33,
  '--text-32': 36,
};

const FOUR_K_TEXT_SIZES: Record<string, number> = {
  '--text-8': 11,
  '--text-9': 12,
  '--text-10': 13.5,
  '--text-11': 15,
  '--text-12': 16,
  '--text-13': 17,
  '--text-14': 19,
  '--text-15': 20,
  '--text-16': 22,
  '--text-18': 25,
  '--text-20': 28,
  '--text-22': 31,
  '--text-24': 34,
  '--text-26': 36,
  '--text-28': 39,
  '--text-30': 42,
  '--text-32': 45,
};

function getNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function getRendererDisplayInfo() {
  if (typeof window === 'undefined') {
    return {
      dpr: 1,
      screenWidth: 1920,
      screenHeight: 1080,
      viewportWidth: 1920,
      viewportHeight: 1080,
    };
  }

  const dpr = getNumber(window.devicePixelRatio, 1);
  const screenWidth = getNumber(window.screen?.width, window.innerWidth || 1920);
  const screenHeight = getNumber(window.screen?.height, window.innerHeight || 1080);
  const viewportWidth = getNumber(window.innerWidth, screenWidth);
  const viewportHeight = getNumber(window.innerHeight, screenHeight);

  return { dpr, screenWidth, screenHeight, viewportWidth, viewportHeight };
}

function getElectronDisplayCssSize(display: ElectronDisplay | undefined, axis: 'width' | 'height', scaleFactor: number) {
  if (!display) return 0;

  const bounds = getNumber(display.bounds?.[axis], 0);
  const workArea = getNumber(display.workArea?.[axis], 0);
  const workAreaSize = getNumber(display.workAreaSize?.[axis], 0);
  const rawSize = getNumber(display.size?.[axis], 0);

  // Electron can expose size as physical pixels on some platforms and as DIP on
  // others. If it looks physical, convert it back to CSS pixels for layout
  // comparisons. bounds/workArea are already DIP and remain the safest source.
  const normalizedSize =
    rawSize > 0 && scaleFactor > 1 && rawSize > Math.max(bounds, workArea) * 1.2
      ? rawSize / scaleFactor
      : rawSize;

  return Math.max(bounds, workArea, workAreaSize, normalizedSize);
}

function getElectronDisplayPhysicalSize(display: ElectronDisplay | undefined, axis: 'width' | 'height', scaleFactor: number) {
  if (!display) return 0;

  const bounds = getNumber(display.bounds?.[axis], 0);
  const workArea = getNumber(display.workArea?.[axis], 0);
  const workAreaSize = getNumber(display.workAreaSize?.[axis], 0);
  const rawSize = getNumber(display.size?.[axis], 0);
  const scaledBounds = Math.round(bounds * Math.max(1, scaleFactor));
  const scaledWorkArea = Math.round(workArea * Math.max(1, scaleFactor));
  const scaledWorkAreaSize = Math.round(workAreaSize * Math.max(1, scaleFactor));

  return Math.max(rawSize, scaledBounds, scaledWorkArea, scaledWorkAreaSize);
}

function buildDisplayRuntimeProfile(electronInfo?: ElectronDisplayInfo | null): DisplayRuntimeProfile {
  const renderer = getRendererDisplayInfo();
  const currentDisplay = electronInfo?.current ?? electronInfo?.primary;
  const hasCurrentElectronDisplay = Boolean(currentDisplay);
  const electronScaleFactor = getNumber(currentDisplay?.scaleFactor, 1);

  const electronDisplayWidth = getElectronDisplayCssSize(
    currentDisplay,
    'width',
    electronScaleFactor,
  );
  const electronDisplayHeight = getElectronDisplayCssSize(
    currentDisplay,
    'height',
    electronScaleFactor,
  );

  const rendererPhysicalWidth = Math.round(renderer.viewportWidth * renderer.dpr);
  const rendererPhysicalHeight = Math.round(renderer.viewportHeight * renderer.dpr);
  const electronPhysicalWidth = getElectronDisplayPhysicalSize(
    currentDisplay,
    'width',
    electronScaleFactor,
  );
  const electronPhysicalHeight = getElectronDisplayPhysicalSize(
    currentDisplay,
    'height',
    electronScaleFactor,
  );

  // In Electron mixed-DPI setups, window.screen and window.devicePixelRatio can
  // follow the primary display for a short time, or stay stale after a monitor
  // move. When main process supplies the current display, use it as the source
  // of truth so moving a 4K-primary app to an FHD monitor drops back to normal
  // density instead of keeping the 4K text scale.
  const densityScreenWidth = hasCurrentElectronDisplay
    ? electronDisplayWidth
    : renderer.screenWidth;
  const densityScreenHeight = hasCurrentElectronDisplay
    ? electronDisplayHeight
    : renderer.screenHeight;

  const physicalScreenWidth = hasCurrentElectronDisplay
    ? electronPhysicalWidth
    : Math.max(rendererPhysicalWidth, renderer.screenWidth);
  const physicalScreenHeight = hasCurrentElectronDisplay
    ? electronPhysicalHeight
    : Math.max(rendererPhysicalHeight, renderer.screenHeight);

  const cssLongEdge = Math.max(
    renderer.viewportWidth,
    renderer.viewportHeight,
    densityScreenWidth,
    densityScreenHeight,
  );
  const cssShortEdge = Math.min(
    Math.max(renderer.viewportWidth, densityScreenWidth || renderer.viewportWidth),
    Math.max(renderer.viewportHeight, densityScreenHeight || renderer.viewportHeight),
  );
  const physicalLongEdge = Math.max(physicalScreenWidth, physicalScreenHeight);
  const physicalShortEdge = Math.min(physicalScreenWidth, physicalScreenHeight);

  const compactViewport = renderer.viewportWidth <= 1440 || renderer.viewportHeight <= 900;

  const native4kViewport = cssLongEdge >= 3200 && cssShortEdge >= 1700;
  const physical4kDisplay = physicalLongEdge >= 3800 && physicalShortEdge >= 2000;
  const largeDisplay = cssLongEdge >= 2500 || physicalLongEdge >= 3000;

  let displayDensity: DisplayRuntimeProfile['displayDensity'] = 'normal';
  let typographyScale = 1;

  // Native 3840 x 2160 can arrive in two ways:
  // 1) the renderer viewport/screen is really 3840 x 2160, or
  // 2) Windows/Electron reports 2560 x 1440 CSS pixels with scaleFactor 1.5.
  // Treat both as a true 4K UI density, but only when the current display really
  // is that high-density monitor.
  if (native4kViewport || physical4kDisplay) {
    displayDensity = '4k';
    typographyScale = 1.42;
  } else if (largeDisplay) {
    displayDensity = 'large';
    typographyScale = 1.18;
  } else if (compactViewport) {
    displayDensity = 'compact';
    typographyScale = 0.92;
  }

  const uiScale = clamp(
    displayDensity === '4k'
      ? 1.42
      : displayDensity === 'large'
        ? 1.2
        : displayDensity === 'compact'
          ? 0.92
          : 1,
    0.9,
    1.45,
  );

  return {
    initializedAt: Date.now(),
    devicePixelRatio: renderer.dpr,
    screenWidth: renderer.screenWidth,
    screenHeight: renderer.screenHeight,
    viewportWidth: renderer.viewportWidth,
    viewportHeight: renderer.viewportHeight,
    electronScaleFactor,
    electronDisplayWidth,
    electronDisplayHeight,
    physicalScreenWidth,
    physicalScreenHeight,
    uiScale,
    typographyScale,
    displayDensity,
    ffrChartScale: typographyScale,
  };
}

let runtimeProfile = buildDisplayRuntimeProfile();

export function getDisplayRuntimeProfile() {
  return runtimeProfile;
}

function chooseTextSizes(profile: DisplayRuntimeProfile) {
  if (profile.displayDensity === '4k') return FOUR_K_TEXT_SIZES;
  if (profile.displayDensity === 'large') return LARGE_TEXT_SIZES;
  if (profile.displayDensity === 'compact') return COMPACT_TEXT_SIZES;
  return BASE_TEXT_SIZES;
}

function applyTextAliases(root: HTMLElement) {
  root.style.setProperty('--text-xs', 'var(--text-12)');
  root.style.setProperty('--text-sm', 'var(--text-14)');
  root.style.setProperty('--text-md', 'var(--text-16)');
  root.style.setProperty('--text-lg', 'var(--text-18)');
  root.style.setProperty('--text-xl', 'var(--text-20)');
  root.style.setProperty('--text-xxl', 'var(--text-22)');

  root.style.setProperty('--text-t1', 'var(--text-12)');
  root.style.setProperty('--text-t2', 'var(--text-14)');
  root.style.setProperty('--text-t3', 'var(--text-16)');
  root.style.setProperty('--text-t4', 'var(--text-18)');
  root.style.setProperty('--text-t5', 'var(--text-20)');
  root.style.setProperty('--text-t6', 'var(--text-22)');
  root.style.setProperty('--text-t7', 'var(--text-24)');
  root.style.setProperty('--text-t8', 'var(--text-28)');

  root.style.setProperty('--mp-font-size-t1', 'var(--text-t1)');
  root.style.setProperty('--mp-font-size-t2', 'var(--text-t2)');
  root.style.setProperty('--mp-font-size-t3', 'var(--text-t3)');
  root.style.setProperty('--mp-font-size-t4', 'var(--text-t4)');
  root.style.setProperty('--mp-font-size-t5', 'var(--text-t5)');
  root.style.setProperty('--mp-font-size-t6', 'var(--text-t6)');
  root.style.setProperty('--mp-font-size-t7', 'var(--text-t7)');
  root.style.setProperty('--mp-font-size-t8', 'var(--text-t8)');
}

export function applyDisplayRuntimeProfileToDocument(electronInfo?: ElectronDisplayInfo | null) {
  if (typeof document === 'undefined') return runtimeProfile;

  runtimeProfile = buildDisplayRuntimeProfile(electronInfo);
  const root = document.documentElement;
  const textSizes = chooseTextSizes(runtimeProfile);

  root.dataset.displayDensity = runtimeProfile.displayDensity;
  root.dataset.display4k = runtimeProfile.displayDensity === '4k' ? 'true' : 'false';

  Object.entries(textSizes).forEach(([name, value]) => {
    root.style.setProperty(name, `${roundHalf(value)}px`);
  });

  applyTextAliases(root);

  const bodySize =
    runtimeProfile.displayDensity === '4k'
      ? 20
      : runtimeProfile.displayDensity === 'large'
        ? 17
        : runtimeProfile.displayDensity === 'compact'
          ? 13
          : 14;

  root.style.setProperty('--font-size', `${bodySize}px`);
  root.style.setProperty('--top-menu-height', runtimeProfile.displayDensity === '4k' ? '62px' : runtimeProfile.displayDensity === 'large' ? '52px' : '44px');
  root.style.setProperty('--title-bar-height', runtimeProfile.displayDensity === '4k' ? '62px' : runtimeProfile.displayDensity === 'large' ? '52px' : '44px');
  root.style.setProperty('--display-ui-scale', runtimeProfile.uiScale.toFixed(3));
  root.style.setProperty('--display-typography-scale', runtimeProfile.typographyScale.toFixed(3));

  return runtimeProfile;
}

export async function applyDisplayRuntimeProfileToDocumentAsync() {
  let electronInfo: ElectronDisplayInfo | null = null;

  try {
    if (typeof window === 'undefined') {
      return applyDisplayRuntimeProfileToDocument(null);
    }

    const getDisplayInfo = window.electron?.ipcRenderer?.getDisplayInfo;
    if (typeof getDisplayInfo === 'function') {
      electronInfo = await getDisplayInfo();
    }
  } catch {
    electronInfo = null;
  }

  return applyDisplayRuntimeProfileToDocument(electronInfo);
}
